import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useTranslation } from 'next-i18next'
import { ShareBox } from '~/components/molecules/share-box'
import { Button } from '~/components/ui/button'
import { SpaceCapacity } from '~/domains/spaces/components/atoms/space-capacity'
import { SpaceDeskCapacity } from '~/domains/spaces/components/atoms/space-desk-capacity'
import { SpaceFavoriteButton } from '~/domains/spaces/components/atoms/space-favorite-button'
import { SpaceFullAddress } from '~/domains/spaces/components/atoms/space-full-address'
import { SpaceSurface } from '~/domains/spaces/components/atoms/space-surface'
import { SpaceHours } from '~/domains/spaces/components/space-details/space-hours'
import { SpaceSectionContent } from '~/domains/spaces/components/space-details/space-section-content'
import { dialogsEmitter } from '~/domains/spaces/emitters/dialog.emitter'
import { SpaceSectionTitle } from './space-section-title'

export const SpaceDescription = ({ space }: { space: Space }) => {
	const { t } = useTranslation()

	return (
		<SpaceSectionContent>
			<SpaceSectionTitle>
				{t('space:sections.description.title.long')}
			</SpaceSectionTitle>

			<div className='flex flex-col gap-8 lg:flex-row'>
				<div className='w-full whitespace-pre-line text-justify lg:w-2/3'>
					{space.description}
				</div>

				<div className='w-full lg:w-1/3'>
					<SpaceHours space={space} />
				</div>
			</div>
		</SpaceSectionContent>
	)
}

export const SpaceDetailsDescription = ({
	space,
	isDesk,
}: {
	space: Space
	isDesk?: boolean
}) => {
	const { t } = useTranslation()

	return (
		<SpaceSectionContent>
			<div className='flex flex-col gap-1'>
				<h1 className='flex flex-row items-center font-bold text-4xl'>
					{space.title}

					<div className='ml-auto flex gap-2'>
						<SpaceFavoriteButton
							space={space}
							iconProps={{ size: 22 }}
							className='text-xl hover:bg-transparent'
						/>

						<ShareBox title={t('space:share.label')} />
					</div>
				</h1>

				<SpaceFullAddress
					as='a'
					href='#location'
					space={space}
					dense={true}
				/>

				{!isDesk && (
					<div className='flex flex-row gap-3'>
						<SpaceCapacity space={space} />

						<SpaceSurface space={space} />

						{!!space.images?.filter((image) => image.type === 'floor_plan')
							.length && (
							<Button
								variant='outline'
								size='sm'
								className='-mt-1 ml-auto'
								onClick={() =>
									dialogsEmitter.emit('spaceCarousel.open', {
										type: 'floor_plan',
									})
								}
							>
								{t('actions:seeSpacePlan.btnLabel')}
							</Button>
						)}
					</div>
				)}

				{isDesk && (
					<div className='flex flex-row gap-3'>
						<SpaceDeskCapacity space={space} />
					</div>
				)}
			</div>

			<SpaceDescription space={space} />
		</SpaceSectionContent>
	)
}
